package com.vamvisions.main.core.presentation.appshell

import androidx.lifecycle.ViewModel
import com.vamvisions.main.core.domain.preferences.ColorScheme
import com.vamvisions.main.core.domain.preferences.Language
import com.vamvisions.main.core.domain.preferences.PreferencesRepository
import com.vamvisions.main.core.domain.preferences.SystemPreferences
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/**
 * Holds the colour scheme and interface language of the whole site. The initial values come from
 * the stored choice, then the system preference, then a default. Storage failures only lose
 * persistence: the state still changes for the current visit.
 */
class AppShellViewModel(
    private val preferences: PreferencesRepository,
    private val systemPreferences: SystemPreferences,
) : ViewModel() {

    private val _state = MutableStateFlow(
        AppShellState(
            colorScheme = initialColorScheme(),
            language = initialLanguage(),
        )
    )
    val state = _state.asStateFlow()

    fun onAction(action: AppShellAction) {
        when (action) {
            AppShellAction.ToggleColorScheme -> toggleColorScheme()
            is AppShellAction.SetLanguage -> setLanguage(action.language)
        }
    }

    private fun toggleColorScheme() {
        val next = when (state.value.colorScheme) {
            ColorScheme.LIGHT -> ColorScheme.DARK
            ColorScheme.DARK -> ColorScheme.LIGHT
        }
        _state.update { it.copy(colorScheme = next) }
        runCatching { preferences.setColorScheme(next) }
    }

    private fun setLanguage(language: Language) {
        _state.update { it.copy(language = language) }
        runCatching { preferences.setLanguage(language) }
    }

    private fun initialColorScheme(): ColorScheme =
        runCatching { preferences.getColorScheme() }.getOrNull()
            ?: if (systemPreferences.prefersDarkColorScheme) ColorScheme.DARK else ColorScheme.LIGHT

    private fun initialLanguage(): Language =
        runCatching { preferences.getLanguage() }.getOrNull()
            ?: browserLanguage()
            ?: Language.EN

    /** The supported language matching the browser language's primary subtag ("fr-CA" → French), ignoring case. */
    private fun browserLanguage(): Language? {
        val primary = systemPreferences.browserLanguage?.substringBefore('-')?.lowercase() ?: return null
        return Language.fromCode(primary)
    }
}
